import { languages } from '@repo/toolkit/language-types'
import Fuse, { IFuseOptions } from 'fuse.js'

import { CountryData } from '../../types/models/country/country-list'
import { Nationality } from '../../types/models/country/nationality'

export function getCountryFlag(country: string, countryList: CountryData[]): CountryData | null {
  const options: IFuseOptions<CountryData> = {
    keys: [...languages.map((lang) => `translations.${lang}`), 'nativeName'],
    threshold: 0.21,
    isCaseSensitive: false
  }
  const fuse = new Fuse(countryList, options)
  const result = fuse.search(country)

  if (result.length === 0) return null

  // Return the first result which has the highest score (lowest numerical value)
  return result[0].item
}

const normalizeCountry = (value: string) =>
  value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

/**
 * Finds the country of a stored country value (any of its codes or names), without fuzzy matching.
 */
export function findCountry(country: string, countryList: CountryData[]): CountryData | undefined {
  const normalizedCountry = normalizeCountry(country)
  return countryList.find(({ alpha2, countryCode, key, translations, nativeName }) =>
    [alpha2, countryCode, key, ...Object.values(translations), nativeName]
      .filter(Boolean)
      .some((value) => normalizeCountry(value) === normalizedCountry)
  )
}

/**
 * The label of a stored nationality code ("FR" → "Français(e)"), the stored value when unknown.
 */
export function getNationalityLabel(nationality: string, nationalities?: Nationality[]): string {
  return nationalities?.find(({ code }) => code === nationality)?.label ?? nationality
}
